import { validateMediaFile } from "@/lib/mediaUpload";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Typography } from "@mui/material";
import { useRef, useState } from "react";

interface MediaUploadProps {
  file: File | null;
  onFileSelect: (file: File | null) => void;
}

export default function MediaUpload({ file, onFileSelect }: MediaUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    const error = validateMediaFile(selected);
    if (error) {
      setFileError(error);
      // Reset input so user can try again
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setFileError(null);
    onFileSelect(selected);
  };

  const handleRemove = () => {
    setFileError(null);
    onFileSelect(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const preview = file ? URL.createObjectURL(file) : null;
  const isVideo = file?.type.startsWith("video/");

  return (
    <Box sx={{ mt: 1 }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
        onChange={handleChange}
        style={{ display: "none" }}
      />

      {!file ? (
        <Box>
          <IconButton
            onClick={() => inputRef.current?.click()}
            sx={{
              border: "1px dashed rgba(255,255,255,0.15)",
              borderRadius: 2,
              width: 64,
              height: 64,
              color: "text.secondary",
              "&:hover": {
                borderColor: "rgba(79,195,247,0.3)",
                color: "primary.main",
              },
            }}
          >
            <AddAPhotoIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: "rgba(255,255,255,0.3)",
              mt: 0.5,
            }}
          >
            Photo or video (max 10MB)
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: "relative", display: "inline-block" }}>
          {isVideo ? (
            <video
              src={preview!}
              style={{
                width: 120,
                height: 80,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          ) : (
            <img
              src={preview!}
              alt="Preview"
              style={{
                width: 120,
                height: 80,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          )}
          <IconButton
            size="small"
            onClick={handleRemove}
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              backgroundColor: "rgba(0,0,0,0.7)",
              color: "white",
              width: 20,
              height: 20,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.9)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      )}

      {fileError && (
        <Typography sx={{ fontSize: "0.75rem", color: "error.main", mt: 0.5 }}>
          {fileError}
        </Typography>
      )}
    </Box>
  );
}
